import _ from 'lodash';
import {DateTime, Duration, Interval} from 'luxon';
import React from 'react';

import {ensureValidInterval} from '../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {Plural, Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {DateTimeFormatterBase} from '../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {
  CalendarWeekdayToShortName,
  calendarWeekdayToWeekdayNumber,
  getSortedWeekdayNames,
  weekdayNumberToCalendarWeekdayMap,
} from '../../../srcCommon/helpers/calendarHelpers';
import {CalendarWeekDaysEnum} from '../../models/api/calendarCommonTypes';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkModel,
  SchedulingLinkRepeatingAvailabilityBlockModel,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../models/api/schedulingLinkModel';

/*
 * Constants.
 */

// Example: Tue, Jul 28
const standardDateFormatOptions: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
};

const referenceDate = DateTime.local();

export enum TrueAvailabilityStatusesEnum {
  UNKNOWN = 'UNKNOWN',
  LOADING = 'LOADING',
  NO_AVAILABILITY = 'NO_AVAILABILITY',
  HAS_AVAILABILITY = 'HAS_AVAILABILITY',
  PAST_AVAILABILITY = 'PAST_AVAILABILITY',
}

/*
 * Api.
 */

export function buildAvailableSlotsSummary(
  schedulingLink: SchedulingLinkModel | DraftSchedulingLinkModel,
  calendarWeekStartDay: CalendarWeekDaysEnum,
) {
  if (schedulingLink.availability.blocks.length === 0) {
    return <Trans id="schedulinglink-availability-empty">No available meeting times</Trans>;
  }

  if (schedulingLink.availability.type === SchedulingLinkAvailabilityTypesEnum.STANDARD) {
    return buildStandardAvailabilitySummary(schedulingLink.availability.blocks);
  }

  if (schedulingLink.availability.type === SchedulingLinkAvailabilityTypesEnum.REPEATING) {
    return buildRepeatingAvailabilitySummary(schedulingLink.availability.blocks, calendarWeekStartDay);
  }

  return null;
}

// Builds a standard availability summary. Examples:
// - "22 hours from Tue, Jul 28th - Thu, Aug 6th"
// - "4 hours on Tue, Jul 28th"
export function buildStandardAvailabilitySummary(
  availabilityBlocks: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>,
) {
  const start = Math.min(...availabilityBlocks.map((block) => block.start));
  const end = Math.max(...availabilityBlocks.map((block) => block.end));
  const durationMs = availabilityBlocks.reduce((acc, block) => acc + (block.end - block.start), 0);
  const durationHours = Duration.fromMillis(durationMs).as('hours');

  const startDateTime = DateTime.fromMillis(start);
  const endDateTime = DateTime.fromMillis(end);
  const isOnSingleDay = startDateTime.hasSame(endDateTime, 'day');

  if (!startDateTime.isValid || !endDateTime.isValid) {
    return null;
  }

  const formattedStart = <DateTimeFormatterBase value={startDateTime} format={standardDateFormatOptions} />;
  const formattedEnd = <DateTimeFormatterBase value={endDateTime} format={standardDateFormatOptions} />;

  if (isOnSingleDay) {
    return (
      <Plural
        id="scheduling-link-standard-availability-single-day"
        value={durationHours}
        one={<># hour on {formattedStart}</>}
        other={<># hours on {formattedStart}</>}
      />
    );
  }

  return (
    <Plural
      id="scheduling-link-standard-availability-multiple-days"
      value={durationHours}
      one={
        <>
          # hour from {formattedStart} - {formattedEnd}
        </>
      }
      other={
        <>
          # hours from {formattedStart} - {formattedEnd}
        </>
      }
    />
  );
}

// Builds a repeating availability summary, e.g. "20 hours every week on Mon, Tues, Wed".
export function buildRepeatingAvailabilitySummary(
  availabilityBlocks: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>,
  calendarWeekStartDay: CalendarWeekDaysEnum,
) {
  const durationMinutes = availabilityBlocks.reduce((acc, block) => acc + block.durationMinutes, 0);
  const durationHours = durationMinutes / 60;

  // Sort weekdays by the teammate's calendarWeekStartDay preference
  const sortedWeekdays = getSortedWeekdayNames(calendarWeekStartDay);
  const sortedAvailabilityDays = _(availabilityBlocks)
    .map((block) => block.weekDay)
    .uniq()
    .sortBy((weekDay) => sortedWeekdays.indexOf(weekDay))
    .value();

  // TODO: figure out how to do proper i18n translation of the interpolated list of availability days.
  const availabilityDaysText = sortedAvailabilityDays
    .map((day) => _.startCase(CalendarWeekdayToShortName[day]))
    .join(', ');
  return (
    <Plural
      id="schedulinglink-repeating-availability"
      value={durationHours}
      one={`# hour every week on ${availabilityDaysText}`}
      other={`# hours every week on ${availabilityDaysText}`}
    />
  );
}

export function buildIntervalFromStandardAvailabilityBlock(
  block: SchedulingLinkStandardAvailabilityBlockModel,
): Interval<true> {
  return ensureValidInterval(
    Interval.fromDateTimes(DateTime.fromMillis(block.start), DateTime.fromMillis(block.end)),
  );
}

export function buildStandardAvailabilityBlockFromInterval(
  interval: Interval<true>,
): SchedulingLinkStandardAvailabilityBlockModel {
  return {
    start: interval.start.toMillis(),
    end: interval.end.toMillis(),
  };
}

export function buildIntervalFromRepeatingAvailabilityBlock(
  block: SchedulingLinkRepeatingAvailabilityBlockModel,
) {
  const {hour, minute, timezone, durationMinutes, weekDay} = block;
  const weekday = calendarWeekdayToWeekdayNumber[weekDay];
  const startDate = referenceDate.setZone(timezone).set({hour, minute, weekday});
  return Interval.after(startDate, {minutes: durationMinutes});
}

export function buildRepeatingAvailabilityBlockFromInterval(
  interval: Interval<true>,
): SchedulingLinkRepeatingAvailabilityBlockModel {
  return {
    hour: interval.start.hour,
    minute: interval.start.minute,
    timezone: interval.start.zoneName,
    durationMinutes: interval.toDuration('minute').minutes,
    weekDay: weekdayNumberToCalendarWeekdayMap[interval.start.weekday],
  };
}
